import { QuestionCircleOutlined } from '@ant-design/icons';
import { useSuppressionListsCRUDActions } from '@mm/state/actions/SuppressionListsCRUDActions';
import { selectKlaviyoSuppressionList } from '@mm/state/modules/suppressionListsTableState/selectors';
import { Switch } from 'antd';
import classNames from 'classnames';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { HelpCenter } from '../../../../../../../../common/components/HelpCenter/HelpCenter';
import styles from './KlaviyoIntegrations.module.css';

type Props = {
  controlsDisabled: boolean;
  tokenType: 'api_key' | 'oauth_token';
  tokenId: number;
  activeFbPageId: number;
  helpLink?: string;
  withNavLink?: boolean;
};

export const KlaviyoSuppressionSetup = ({
  controlsDisabled,
  tokenType,
  tokenId,
  activeFbPageId,
  helpLink,
  withNavLink = true,
}: Props) => {
  const { update, create, retrieveAll } = useSuppressionListsCRUDActions();
  const suppressions = useSelector(selectKlaviyoSuppressionList);
  const suppression = suppressions[0];
  const sendSignal = suppression?.data ? suppression.data?.sendSignal : true;

  const updateSuppression = (enabled: boolean, sendSignal: boolean) => {
    const tokenKey =
      tokenType === 'api_key' ? 'klaviyo_api_key' : 'klaviyo_oauth';
    if (suppression?.id) {
      update(activeFbPageId, {
        id: suppression.id,
        enabled,
        data: { sendSignal },
        sourceId: suppression.sourceId,
        sourceKind: tokenKey,
      });
    } else {
      create(activeFbPageId, {
        enabled,
        data: { sendSignal },
        sourceId: tokenId,
        sourceKind: tokenKey,
      });
    }
  };

  useEffect(() => {
    if (suppressions.length === 0) {
      retrieveAll(activeFbPageId);
    }
  }, []);

  const scrollToElement = (selector: string) => {
    document.getElementById(selector)?.scrollIntoView({
      behavior: 'smooth',
    });
  };

  const onNavLinkClick: React.MouseEventHandler<HTMLAnchorElement> = e => {
    e.preventDefault();
    const el = e.target as Element;
    const href = el?.getAttribute('href');
    if (href) {
      const withoutHash = href.split('#')[1];
      scrollToElement(withoutHash);
    }
  };

  return (
    <div
      className={classNames(styles.box, styles.suppressionBox, {
        [styles.boxDisabled]: controlsDisabled,
      })}
    >
      <div className={classNames(styles.boxWithHelp)}>
        <div className={styles.boxContent}>
          <div className={classNames(styles.heading, styles.headingWithSwitch)}>
            Automatically suppress Klaviyo contacts
            <Switch
              key={`switch-suppression-${
                suppression?.enabled ? 'enabled' : 'disabled'
              }`}
              defaultChecked={!!suppression?.enabled}
              checkedChildren="Yes"
              unCheckedChildren="No"
              className={styles.switch}
              onChange={ok => updateSuppression(ok, !!sendSignal)}
            />
          </div>
        </div>
        {helpLink === undefined ? null : (
          <HelpCenter helpLink={helpLink} className={styles.help} />
        )}
      </div>
      {withNavLink && (
        <div className={styles.suppressionLinks}>
          <a
            className={styles.navLink}
            href="#contact-suppression"
            onClick={onNavLinkClick}
          >
            Go to Suppression Settings
          </a>
          <a
            className={styles.questionMarkHelpLink}
            target="_blank"
            rel="noopener noreferrer"
            href="https://customers.ai/help/l/en/article/uhl81gxos2-enable-email-suppression-list"
          >
            <QuestionCircleOutlined />
          </a>
        </div>
      )}
    </div>
  );
};
